import React, { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { ChatPanel } from "@/components/ChatPanel";
import { AdminReviews } from "@/components/AdminReviews";
import { AdminEmployees } from "@/components/AdminEmployees";
import { AdminAnalytics } from "@/components/AdminAnalytics";
import { PurgeDataButton } from "@/components/PurgeDataButton";
import { ProgressTracker, PHASES } from "@/components/ProgressTracker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { format, parseISO } from "date-fns";
import { Users, Trash2, MessageSquare, Loader2, Star, BarChart3, PhoneCall } from "lucide-react";

export default function AdminDashboard() {
  const [projects, setProjects] = useState([]);
  const [active, setActive] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [deposit, setDeposit] = useState("");
  const [finalAmt, setFinalAmt] = useState("");

  const loadEmployees = () => api.get("/admin/employees").then(({ data }) => setEmployees(data)).catch(() => {});

  const loadProjects = async () => {
    const { data } = await api.get("/admin/projects");
    setProjects(data);
    if (active) {
      const fresh = data.find((p) => p.id === active.id);
      if (fresh) setActive(fresh);
    }
  };

  useEffect(() => {
    loadProjects();
    loadEmployees();
    const t = setInterval(loadProjects, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  const openProject = (p) => { setActive(p); setDeposit(String(p.deposit_amount || "")); setFinalAmt(String(p.final_amount || "")); loadEmployees(); };

  const [confirmDelProject, setConfirmDelProject] = useState(null);
  const deleteProject = async () => {
    if (!confirmDelProject) return;
    try {
      await api.delete(`/admin/projects/${confirmDelProject.id}`);
      toast.success("Client deleted");
      if (active?.id === confirmDelProject.id) setActive(null);
      setConfirmDelProject(null);
      loadProjects();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveDeposit = async () => {
    try {
      const { data } = await api.patch(`/admin/projects/${active.id}`, { deposit_amount: parseFloat(deposit) || 0 });
      setActive(data);
      toast.success("Deposit amount updated");
      loadProjects();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleDepositPaid = async (val) => {
    try {
      const { data } = await api.patch(`/admin/projects/${active.id}`, { deposit_paid: val });
      setActive(data);
      toast.success(val ? "Marked deposit as paid" : "Deposit marked unpaid");
      loadProjects();
      loadEmployees();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveFinal = async () => {
    try {
      const { data } = await api.patch(`/admin/projects/${active.id}`, { final_amount: parseFloat(finalAmt) || 0 });
      setActive(data);
      toast.success("Final payment amount updated");
      loadProjects();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleFinalPaid = async (val) => {
    try {
      const { data } = await api.patch(`/admin/projects/${active.id}`, { final_paid: val });
      setActive(data);
      toast.success(val ? "Marked final payment as paid" : "Final payment marked unpaid");
      loadProjects();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const assignReferrer = async (employeeId) => {
    try {
      const { data } = await api.post(`/admin/projects/${active.id}/assign-referrer`, {
        employee_id: employeeId === "none" ? null : employeeId,
      });
      setActive(data);
      toast.success("Referrer updated");
      loadEmployees();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const updateStatus = async (status) => {
    try {
      const { data } = await api.patch(`/admin/projects/${active.id}`, { status });
      setActive(data);
      toast.success(`Status set to ${status}`);
      loadProjects();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="grain min-h-screen bg-background text-white">
      <Header onSchedule={() => {}} />
      <div className="mx-auto max-w-7xl px-6 py-8">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">// Admin Control</p>
            <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">Command Center</h1>
          </div>
          <PurgeDataButton onDone={() => { loadProjects(); loadEmployees(); }} />
        </div>

        <Tabs defaultValue="projects">
          <TabsList className="h-auto rounded-none border border-white/10 bg-[#0A0A0B] p-1">
            <TabsTrigger data-testid="tab-projects" value="projects" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-white">
              <Users className="mr-2 h-4 w-4" /> Clients & Projects
            </TabsTrigger>
            <TabsTrigger data-testid="tab-reviews" value="reviews" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-white">
              <Star className="mr-2 h-4 w-4" /> Reviews
            </TabsTrigger>
            <TabsTrigger data-testid="tab-employees" value="employees" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-white">
              <Users className="mr-2 h-4 w-4" /> Employees
            </TabsTrigger>
            <TabsTrigger data-testid="tab-analytics" value="analytics" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-white">
              <BarChart3 className="mr-2 h-4 w-4" /> Analytics
            </TabsTrigger>
          </TabsList>

          <TabsContent value="projects" className="mt-4">
            <div className="border border-white/10 bg-[#0A0A0B]">
              <div className="grid grid-cols-12 border-b border-white/10 px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-zinc-500">
                <div className="col-span-3">Client</div>
                <div className="col-span-3">Project</div>
                <div className="col-span-2">Callback</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2 text-right">Account</div>
              </div>
              {projects.length === 0 && <p className="p-6 text-sm text-zinc-500">No submissions yet.</p>}
              {projects.map((p) => (
                <div key={p.id} data-testid={`admin-project-${p.id}`} onClick={() => openProject(p)}
                  className="grid w-full cursor-pointer grid-cols-12 items-center border-b border-white/5 px-4 py-4 text-left text-sm transition-colors hover:bg-white/[0.03]">
                  <div className="col-span-3 overflow-hidden">
                    <p className="truncate font-medium">{p.name}</p>
                    <p className="truncate font-mono text-[11px] text-zinc-500">{p.email}</p>
                  </div>
                  <div className="col-span-3 truncate text-zinc-300">{p.business_name || p.what_needs_made?.slice(0, 30)}</div>
                  <div className="col-span-2 font-mono text-[12px] text-zinc-400">
                    {p.callback_at ? format(parseISO(p.callback_at), "MMM d") : "Callback"}
                  </div>
                  <div className="col-span-2">
                    <Badge className="rounded-sm bg-white/5 font-mono text-[10px] uppercase text-primary">{p.status}</Badge>
                  </div>
                  <div className="col-span-2 flex items-center justify-end gap-2">
                    {p.user_id ? <Badge className="rounded-sm bg-emerald-500/15 text-[10px] text-emerald-400">Registered</Badge>
                      : <Badge className="rounded-sm bg-white/5 text-[10px] text-zinc-400">Lead</Badge>}
                    <button
                      data-testid={`delete-client-${p.id}`}
                      onClick={(e) => { e.stopPropagation(); setConfirmDelProject(p); }}
                      className="text-zinc-500 transition-colors hover:text-destructive"
                      title="Delete client"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="reviews" className="mt-4">
            <AdminReviews />
          </TabsContent>

          <TabsContent value="employees" className="mt-4">
            <AdminEmployees />
          </TabsContent>

          <TabsContent value="analytics" className="mt-4">
            <AdminAnalytics />
          </TabsContent>
        </Tabs>
      </div>

      {/* Project detail sheet */}
      <Sheet open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <SheetContent data-testid="admin-project-sheet"
          className="w-full overflow-y-auto border-l border-white/10 bg-[#08080A] text-white sm:max-w-2xl">
          {active && (
            <div className="space-y-6 pt-4">
              <div>
                <h2 className="font-display text-2xl font-bold">{active.name}</h2>
                <p className="font-mono text-xs text-zinc-500">{active.email} · {active.phone}</p>
              </div>

              <div className="border border-white/10 bg-black p-4">
                <p className="font-mono text-[11px] uppercase text-zinc-500">Brief</p>
                <p className="mt-2 whitespace-pre-wrap text-sm text-zinc-200">{active.what_needs_made}</p>
                <div className="mt-3 grid grid-cols-2 gap-2 font-mono text-[11px] text-zinc-500">
                  <span>BUSINESS: {active.business_name || "—"}</span>
                  <span>DOMAIN: {active.domain_name || (active.has_domain ? "yes" : "none")}</span>
                  <span>COLORS: {active.color_scheme || "—"}</span>
                  <span>LOGO: {active.has_logo ? "yes" : "no"}</span>
                  <span>REFERRED BY: {active.referred_by || "—"}</span>
                </div>
              </div>

              <div className="border border-white/10 bg-black p-4">
                <p className="mb-4 font-mono text-[11px] uppercase text-zinc-500">Progress</p>
                <ProgressTracker status={active.status} />
                <div className="mt-5">
                  <Label className="text-zinc-400">Update status</Label>
                  <Select value={active.status} onValueChange={updateStatus}>
                    <SelectTrigger data-testid="status-select" className="mt-1.5 rounded-sm border-white/10 bg-black">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-[#0A0A0B] text-white">
                      {PHASES.map((ph) => (
                        <SelectItem key={ph} value={ph} data-testid={`status-option-${ph}`}>{ph}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="border border-white/10 bg-black p-4">
                <p className="font-mono text-[11px] uppercase text-zinc-400">Referral &amp; Deposit</p>
                <div className="mt-3">
                  <Label className="text-zinc-400">Referred by (employee)</Label>
                  <Select value={active.referrer_employee_id || "none"} onValueChange={assignReferrer}>
                    <SelectTrigger data-testid="referrer-select" className="mt-1.5 rounded-sm border-white/10 bg-black">
                      <SelectValue placeholder="Assign an employee" />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-[#0A0A0B] text-white">
                      <SelectItem value="none" data-testid="referrer-option-none">No employee</SelectItem>
                      {employees.map((e) => (
                        <SelectItem key={e.id} value={e.id} data-testid={`referrer-option-${e.id}`}>{e.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="mt-4 flex items-end gap-2">
                  <div className="flex-1">
                    <Label className="text-zinc-400">Deposit amount ($)</Label>
                    <Input data-testid="deposit-input" type="number" value={deposit} onChange={(e) => setDeposit(e.target.value)}
                      className="mt-1.5 rounded-sm border-white/10 bg-black" placeholder="0" />
                  </div>
                  <Button data-testid="save-deposit-btn" onClick={saveDeposit}
                    className="rounded-sm bg-primary text-white hover:bg-primary/90">Save</Button>
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <div>
                    <Label className="text-sm text-zinc-300">Deposit paid</Label>
                    <p className="text-xs text-zinc-500">Marking paid credits the referrer +${100}.</p>
                  </div>
                  <Switch data-testid="deposit-paid-switch" checked={!!active.deposit_paid} onCheckedChange={toggleDepositPaid} />
                </div>

                <div className="mt-4 flex items-end gap-2 border-t border-white/10 pt-4">
                  <div className="flex-1">
                    <Label className="text-zinc-400">Final payment amount ($)</Label>
                    <Input data-testid="final-input" type="number" value={finalAmt} onChange={(e) => setFinalAmt(e.target.value)}
                      className="mt-1.5 rounded-sm border-white/10 bg-black" placeholder="0" />
                  </div>
                  <Button data-testid="save-final-btn" onClick={saveFinal}
                    className="rounded-sm bg-primary text-white hover:bg-primary/90">Save</Button>
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <div>
                    <Label className="text-sm text-zinc-300">Final payment paid</Label>
                    <p className="text-xs text-zinc-500">Mark once the client has paid the balance.</p>
                  </div>
                  <Switch data-testid="final-paid-switch" checked={!!active.final_paid} onCheckedChange={toggleFinalPaid} />
                </div>
              </div>

              <div className="border border-white/10 bg-black p-4">
                <div className="mb-3 flex items-center gap-2 text-primary"><MessageSquare className="h-4 w-4" />
                  <p className="font-mono text-[11px] uppercase text-zinc-400">Direct Message</p></div>
                <ChatPanel projectId={active.id} meRole="admin" />
              </div>

              <div className="border border-white/10 bg-black p-4">
                <div className="mb-3 flex items-center gap-2 text-primary"><PhoneCall className="h-4 w-4" />
                  <p className="font-mono text-[11px] uppercase text-zinc-400">Callback Request</p></div>
                <p className="text-sm text-zinc-200">
                  Call <span className="text-white">{active.phone || "—"}</span>
                  {active.callback_at && <> on <span className="text-white">{format(parseISO(active.callback_at), "EEE, MMM d, yyyy")}</span></>}.
                </p>
                {active.callback_note && <p className="mt-1 text-sm text-zinc-400">{active.callback_note}</p>}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!confirmDelProject} onOpenChange={(v) => !v && setConfirmDelProject(null)}>
        <AlertDialogContent data-testid="delete-client-dialog" className="border-white/10 bg-[#0A0A0B] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this client?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This permanently removes <span className="text-white">{confirmDelProject?.name}</span> ({confirmDelProject?.email}),
              their project and all messages. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/15 bg-transparent text-zinc-300 hover:bg-white/5 hover:text-white">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-client-btn" onClick={deleteProject}
              className="bg-destructive text-white hover:bg-destructive/90">Delete client</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
